import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlarmClock, AlertTriangle, BadgeCheck, Bell, CheckCheck, Flame, Loader2, MessageSquare, PiggyBank, RotateCcw, Send, UserPlus } from 'lucide-react';
import notificationService from '@services/notificationService';
import { usePermissions } from '@hooks/usePermissions';
import { useActivityModal } from '@contexts/ActivityModalContext';
import type { AppNotification, NotificationSummary, NotificationType } from '@/types/planning';
import { formatDateBR } from '@/utils/formatters';

const POLL_MS = 60 * 1000;

const TYPE_STYLE: Record<NotificationType, { icon: React.ReactNode; bubble: string; label: string }> = {
  overdue: { icon: <AlertTriangle size={14} className="text-red-600" />, bubble: 'bg-red-100', label: 'Atrasada' },
  due_today: { icon: <AlarmClock size={14} className="text-amber-600" />, bubble: 'bg-amber-100', label: 'Vence hoje' },
  critical: { icon: <Flame size={14} className="text-rose-600" />, bubble: 'bg-rose-100', label: 'Crítica' },
  assigned: { icon: <UserPlus size={14} className="text-blue-600" />, bubble: 'bg-blue-100', label: 'Delegada a você' },
  ai_budget: { icon: <PiggyBank size={14} className="text-amber-700" />, bubble: 'bg-amber-100', label: 'Orçamento de IA' },
  approval_requested: { icon: <Send size={14} className="text-amber-700" />, bubble: 'bg-amber-100', label: 'Post para aprovar' },
  changes_requested: { icon: <RotateCcw size={14} className="text-rose-600" />, bubble: 'bg-rose-100', label: 'Ajustes no post' },
  post_approved: { icon: <BadgeCheck size={14} className="text-emerald-600" />, bubble: 'bg-emerald-100', label: 'Post aprovado' },
  post_comment: { icon: <MessageSquare size={14} className="text-sky-600" />, bubble: 'bg-sky-100', label: 'Comentário no post' },
  post_reminder: { icon: <AlarmClock size={14} className="text-pink-600" />, bubble: 'bg-pink-100', label: 'Hora de publicar' },
  post_failed: { icon: <AlertTriangle size={14} className="text-red-600" />, bubble: 'bg-red-100', label: 'Falha na publicação' },
};

/**
 * Sino de alertas do header. O contador (GET /notifications/summary) é consultado a cada 60s e a
 * cada mudança de rota; a lista (GET /notifications) só quando o dropdown abre. Clicar num alerta
 * marca como lido e abre a atividade no modal global. Falhas são silenciosas — o sino é
 * não-crítico e não pode derrubar a tela.
 */
export const NotificationBell: React.FC = () => {
  const { checkPermission, isLoading: permissionsLoading } = usePermissions();
  const canRead = !permissionsLoading && checkPermission('notifications.read').hasPermission;
  const { openActivity, version } = useActivityModal();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [loadingList, setLoadingList] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await notificationService.getSummary());
    } catch {
      // Silent fail — notification is non-critical
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      setItems(await notificationService.getAll());
    } catch {
      setItems([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  // Rota nova ou atividade alterada → recontar.
  useEffect(() => {
    if (!canRead) return;
    fetchSummary();
  }, [canRead, location.pathname, version, fetchSummary]);

  // Polling a cada 60s (pausa com a aba oculta).
  useEffect(() => {
    if (!canRead) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchSummary();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [canRead, fetchSummary]);

  if (!canRead) return null;

  const unread = summary?.unread ?? 0;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const handleClick = async (n: AppNotification) => {
    setOpen(false);
    if (!n.read_at) {
      try {
        await notificationService.markRead(n.id);
      } catch {
        /* ignore */
      }
      fetchSummary();
    }
    if (n.social_post_id) {
      setOpen(false);
      navigate(`/social/posts?post=${n.social_post_id}`);
    } else if (n.activity_id) openActivity(n.activity_id);
  };

  const handleReadAll = async () => {
    try {
      await notificationService.markAllRead();
      setItems((prev) => prev?.map((n) => ({ ...n, read_at: n.read_at ?? 'agora' })) ?? prev);
      fetchSummary();
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="relative">
      <button
        className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors relative"
        onClick={toggle}
        title="Alertas"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-[70vh] flex flex-col">
          <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-900">Alertas</p>
              {summary && (
                <p className="text-xs text-slate-500 mt-0.5">
                  <span className={clsx(summary.overdue > 0 && 'text-red-600 font-medium')}>
                    {summary.overdue} atrasada{summary.overdue === 1 ? '' : 's'}
                  </span>
                  {' · '}
                  <span className={clsx(summary.due_today > 0 && 'text-amber-600 font-medium')}>
                    {summary.due_today} vence{summary.due_today === 1 ? '' : 'm'} hoje
                  </span>
                  {summary.critical > 0 && (
                    <>
                      {' · '}
                      <span className="text-rose-600 font-medium">
                        {summary.critical} crítica{summary.critical === 1 ? '' : 's'}
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>
            {unread > 0 && (
              <button
                onClick={handleReadAll}
                className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 whitespace-nowrap"
              >
                <CheckCheck size={14} /> Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="overflow-y-auto custom-scrollbar">
            {loadingList && items === null && (
              <div className="flex items-center justify-center py-6 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin mr-2" /> Carregando…
              </div>
            )}
            {items !== null && items.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-400">Nenhum alerta</div>
            )}
            {items?.map((n) => {
              const style = TYPE_STYLE[n.type] ?? TYPE_STYLE.assigned;
              return (
                <button
                  key={n.id}
                  className={clsx(
                    'w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-t border-slate-100 first:border-t-0',
                    !n.read_at && 'bg-emerald-50/40',
                  )}
                  onClick={() => handleClick(n)}
                >
                  <div className="flex items-start gap-3">
                    <div className={clsx('p-1.5 rounded-full mt-0.5', style.bubble)}>{style.icon}</div>
                    <div className="min-w-0 flex-1">
                      <p className={clsx('text-sm text-slate-900', !n.read_at && 'font-medium')}>{n.message}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {style.label} · {formatDateBR(n.notification_date)}
                      </p>
                    </div>
                    {!n.read_at && <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {open && <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />}
    </div>
  );
};
